import { EventReportTab } from '@components/event-linear-chart-section/src/event-linear-chart-section.type';
import { Actions } from '@components/submenu-with-icon/src/submenu-icon';
import { EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import {
  EventOfferTableFormattedData,
  EventReportActionType,
  EventReportDataType,
  EventSalesAddonsRow,
  EventSalesStatsAddonsResponse,
  EventSalesStatsAdmissions,
  EventSalesStatsResponse,
} from '@domain/event-report';
import { TFunction } from 'i18next';

import {
  EVENT_REPORT_ADDON_TYPE,
  EVENT_REPORT_BOTTLE_SERVICE_TYPE,
  EVENT_REPORT_GUEST_LIST_TYPE,
  EVENT_REPORT_INVITATION_TYPE,
  EVENT_REPORT_PASS_TYPE,
  EVENT_REPORT_TAB_ADDON,
  EVENT_REPORT_TAB_ADMISSION,
  EVENT_REPORT_TAB_INVITATION,
  EVENT_REPORT_TAB_PASS,
  EVENT_REPORT_TABLE_ADDONS_COLUMN,
  EVENT_REPORT_TABLE_DDP_COLUMN,
  EVENT_REPORT_TICKET_TYPE,
  EVENT_REPORT_TYPES,
  OFFER_TABLE_COLUMNS,
  PAST_OFFER_TABLE_COLUMNS,
} from './config';
import { OFFER_TABLE_COLUMNS_TYPES, TableActions } from './types';

const createTotalFromAdmission = ({
  data,
  type,
  bookingFilterValue,
}: {
  data: EventOfferTableFormattedData;
  type: EventReportDataType;
  bookingFilterValue: EventReportBookingOptions;
}) => {
  if (data[type].length) return data[type][0].totalSoldItems[bookingFilterValue];

  return 0;
};

export const getSoldItemsByTabType = ({
  tab,
  data,
  addons,
  bookingFilterValue,
}: {
  tab: EventReportTab;
  data?: EventOfferTableFormattedData;
  addons?: EventSalesAddonsRow[];
  bookingFilterValue: EventReportBookingOptions;
}) => {
  if (!data || !addons) return 0;

  let totalTickets = 0;
  let bottleService = 0;
  let guestList = 0;

  switch (tab.id) {
    case EVENT_REPORT_TAB_ADMISSION:
      totalTickets = createTotalFromAdmission({ data, type: EVENT_REPORT_TICKET_TYPE, bookingFilterValue });
      bottleService = createTotalFromAdmission({ data, type: EVENT_REPORT_BOTTLE_SERVICE_TYPE, bookingFilterValue });
      guestList = createTotalFromAdmission({ data, type: EVENT_REPORT_GUEST_LIST_TYPE, bookingFilterValue });

      return totalTickets + bottleService + guestList;
    case EVENT_REPORT_TAB_ADDON:
      return addons.length ? addons[0].totalSoldItems[bookingFilterValue] : 0;
    case EVENT_REPORT_TAB_PASS:
      return createTotalFromAdmission({ data, type: EVENT_REPORT_PASS_TYPE, bookingFilterValue });
    case EVENT_REPORT_TAB_INVITATION:
      return createTotalFromAdmission({ data, type: EVENT_REPORT_INVITATION_TYPE, bookingFilterValue });
    default:
      return 0;
  }
};

const isOfferTabActive = (data?: EventOfferTableFormattedData) => {
  if (!data) return false;

  const hasTickets = data[EVENT_REPORT_TICKET_TYPE].length > 0;
  const hasBottleService = data[EVENT_REPORT_BOTTLE_SERVICE_TYPE].length > 0;
  const hastGuestList = data[EVENT_REPORT_GUEST_LIST_TYPE].length > 0;

  return hasTickets || hasBottleService || hastGuestList;
};

const isAddonTabActive = (addons?: EventSalesAddonsRow[]) => !!addons && addons.length > 0;

const isPassTabActive = (data?: EventOfferTableFormattedData) => !!data && data[EVENT_REPORT_PASS_TYPE].length > 0;

const isInvitationTabActive = ({
  data,
  bookingFilterValue,
}: {
  data?: EventOfferTableFormattedData;
  bookingFilterValue: EventReportBookingOptions;
}) =>
  !!data &&
  data[EVENT_REPORT_INVITATION_TYPE].length > 0 &&
  data[EVENT_REPORT_INVITATION_TYPE][0].totalSoldItems[bookingFilterValue] > 0;

export const isCurrentTabActive = ({
  tab,
  data,
  addons,
  bookingFilterValue,
}: {
  tab: EventReportTab;
  data?: EventOfferTableFormattedData;
  addons?: EventSalesAddonsRow[];
  bookingFilterValue: EventReportBookingOptions;
}) => {
  switch (tab.id) {
    case EVENT_REPORT_TAB_ADMISSION:
      return isOfferTabActive(data);
    case EVENT_REPORT_TAB_ADDON:
      return isAddonTabActive(addons);
    case EVENT_REPORT_TAB_PASS:
      return isPassTabActive(data);
    case EVENT_REPORT_TAB_INVITATION:
      return isInvitationTabActive({ data, bookingFilterValue });
    default:
      return false;
  }
};

export const buildActionsByType = (type: EventReportActionType, id: string, actions: TableActions): Actions[] => {
  return actions[type].map((element) => ({
    name: element.name,
    action: () => element.method({ id, type }),
  }));
};

export const formatDataOfferTableSection = ({
  data,
  isLiveEvent,
  addons,
  t,
  offersActions,
}: {
  data?: EventSalesStatsResponse;
  isLiveEvent: boolean;
  addons?: EventSalesStatsAddonsResponse;
  t: TFunction<'translation', undefined>;
  offersActions: TableActions;
}) => {
  if (!data || !addons)
    return {
      data: undefined,
      addons: undefined,
      addonsBookings: undefined,
      tabs: [],
      defaultTab: EVENT_REPORT_TAB_ADMISSION,
      tableColumns: [],
    };
  const offerTableColumns = isLiveEvent ? OFFER_TABLE_COLUMNS : PAST_OFFER_TABLE_COLUMNS;
  const columnsWithExpandData = offerTableColumns.map((table) => {
    const hasAddons = data[table.type as keyof EventSalesStatsAdmissions].offers?.[0]?.addOnSales.addOns.length > 0;
    const hasDDP = data[table.type as keyof EventSalesStatsAdmissions].dirittoPrevenditaRevenue.total > 0;

    return {
      ...table,
      columns: table.columns.flatMap((column) => {
        if (column.id === EVENT_REPORT_TABLE_ADDONS_COLUMN && !hasAddons) return [];
        if (column.id === EVENT_REPORT_TABLE_DDP_COLUMN && !hasDDP) return [];
        if (table.expandColumns.includes(column.id as OFFER_TABLE_COLUMNS_TYPES) && hasAddons) {
          return [
            column,
            ...data[table.type as keyof EventSalesStatsAdmissions].offers[0].addOnSales.addOns.map((addon) => ({
              id: `${column.id}-${addon.name}`,
              isActive: false,
              dependsOn: column.id,
              isClickable: true,
            })),
          ];
        }

        return column;
      }),
    };
  });
  const offerTotalQuantities = EVENT_REPORT_TYPES.map((type) => {
    const isNull = data[type as keyof EventSalesStatsAdmissions].offers.some((offer) => !offer.quantity);

    return isNull
      ? null
      : data[type as keyof EventSalesStatsAdmissions].offers.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0);
  });

  const offersData: EventOfferTableFormattedData = EVENT_REPORT_TYPES.reduce(
    (acc: EventOfferTableFormattedData, type: EventReportDataType) => {
      const totalBottleServiceSoldItems = data[type as keyof EventSalesStatsAdmissions].offers.reduce(
        (accum, offer) => ({
          total: accum.total + offer.soldItems.total * (offer.guestCapacity ?? 1),
          online: accum.online + offer.soldItems.online * (offer.guestCapacity ?? 1),
          offline: accum.offline + offer.soldItems.offline * (offer.guestCapacity ?? 1),
        }),
        { total: 0, online: 0, offline: 0 }
      );

      acc[type as keyof EventOfferTableFormattedData] = data[type as keyof EventSalesStatsAdmissions].offers.map(
        (offer) => ({
          totalQuantity: offerTotalQuantities[EVENT_REPORT_TYPES.indexOf(type)],
          totalBottleServiceSoldItems,
          totalSoldItems: data[type as keyof EventSalesStatsAdmissions].soldItems,
          totalRevenue: data[type as keyof EventSalesStatsAdmissions].revenue,
          totalAddOnSoldItems: data[type as keyof EventSalesStatsAdmissions].addOns.soldItems,
          totalDirittoPrevenditaRevenue: data[type as keyof EventSalesStatsAdmissions].dirittoPrevenditaRevenue,
          totalAttendees: data[type as keyof EventSalesStatsAdmissions].attendees,
          totalPassHolders: data.season_pass.passHolders,
          attendees: offer.attendees,
          isActivePass: offer.isActive ?? false,
          passHolders: offer.passHolders ?? undefined,
          color: offer.color,
          name: offer.name,
          description: offer.description,
          price: offer.price,
          quantity: offer.quantity,
          bottleServiceSoldItems: {
            total: offer.soldItems.total * (offer.guestCapacity ?? 1),
            online: offer.soldItems.online * (offer.guestCapacity ?? 1),
            offline: offer.soldItems.offline * (offer.guestCapacity ?? 1),
          },
          soldItems: offer.soldItems,
          revenue: offer.revenue,
          status: offer.status,
          dirittoPrevenditaRevenue: offer.dirittoPrevenditaRevenue,
          addOnSoldItems: offer.addOnSales.soldItems,
          addOns: offer.addOnSales.addOns,
          actions: buildActionsByType(type, offer.id, offersActions),
        })
      );
      return acc;
    },
    { ticket: [], bottleservice: [], guestlist: [], season_pass: [], invitation: [], checkin: [] }
  );

  const addonTotalQuantity = addons?.addOns?.some((a) => a.quantity === null)
    ? null
    : addons.addOns.reduce((sum, a) => sum + (a.quantity ?? 0), 0);

  const addonsData: EventSalesAddonsRow[] = addons?.addOns?.map((addon) => ({
    id: addon.id,
    totalRevenue: addons.revenue,
    totalAttendees: addons.attendees,
    attendees: addon.attendees,
    price: addon.fixedPrice,
    totalSoldItems: addons.soldItems,
    name: addon.name,
    description: addon.description,
    soldItems: addon.soldItems,
    revenue: addon.revenue,
    quantity: addon.quantity,
    totalQuantity: addonTotalQuantity,
    actions: buildActionsByType(EVENT_REPORT_ADDON_TYPE, addon.id, offersActions),
  }));

  return {
    data: offersData,
    addons: addonsData,
    defaultTab: EVENT_REPORT_TAB_ADMISSION,
    tabs: [
      {
        id: EVENT_REPORT_TAB_ADMISSION,
        name: t('eventReport:admissionTabLabel'),
      },
      {
        id: EVENT_REPORT_TAB_ADDON,
        name: t('eventReport:addonTabLabel'),
      },
      {
        id: EVENT_REPORT_TAB_PASS,
        name: t('eventReport:badgePassLabel'),
      },
      {
        id: EVENT_REPORT_TAB_INVITATION,
        name: t('eventReport:badgeInvitationLabel'),
      },
    ],
    tableColumns: columnsWithExpandData,
  };
};
